import { Box, Chip, useTheme, Text } from '@ltvco/refresh-lib/theme';
import { formatPhone, nameize, unformatPhone } from '@ltvco/refresh-lib/utils';

import { PossibleReportParams } from 'components/ChatBV/types';

interface ReportChipProps {
  report: PossibleReportParams;
  onChipClick: (report: PossibleReportParams) => void;
}

const getReportDisplayName = (report: PossibleReportParams): string => {
  const type = nameize(report.search_type);
  const details: string[] = [];

  if (
    report.search_type === 'person' &&
    report.first_name &&
    report.last_name
  ) {
    details.push(`${report.first_name} ${report.last_name}`);
  } else if (report.search_type === 'phone' && report.phone_number) {
    const cleanPhone = unformatPhone(report.phone_number);
    const phoneDigits =
      cleanPhone.length === 11 && cleanPhone.startsWith('1')
        ? cleanPhone.substring(1)
        : cleanPhone;
    const formattedPhone = formatPhone(phoneDigits) || report.phone_number;
    details.push(formattedPhone);
  } else if (report.search_type === 'email' && report.email) {
    details.push(report.email);
  } else if (report.search_type === 'property' && report.address) {
    details.push(report.address);
  } else if (report.search_type === 'vehicle') {
    if (report.vehicle_vin) {
      details.push(`VIN: ${report.vehicle_vin}`);
    } else if (report.vehicle_license_plate) {
      details.push(`Plate: ${report.vehicle_license_plate}`);
    }
  }

  return `${type} (${details.join(', ')})`;
};

export const ReportChip = ({ report, onChipClick }: ReportChipProps) => {
  const theme = useTheme();
  const displayName = getReportDisplayName(report);
  const isPhoneReport = report.search_type === 'phone' && report.phone_number;

  let formattedPhone: string | null = null;
  if (isPhoneReport && report.phone_number) {
    const cleanPhone = unformatPhone(report.phone_number);
    const phoneDigits =
      cleanPhone.length === 11 && cleanPhone.startsWith('1')
        ? cleanPhone.substring(1)
        : cleanPhone;

    if (phoneDigits.length === 10) {
      const formatted = formatPhone(phoneDigits);
      if (formatted && formatted !== phoneDigits) {
        formattedPhone = formatted;
      } else {
        formattedPhone = `(${phoneDigits.substring(
          0,
          3
        )}) ${phoneDigits.substring(3, 6)}-${phoneDigits.substring(6)}`;
      }
    }
  }

  const handleClick = () => {
    onChipClick(report);
  };

  return (
    <Chip
      label={
        isPhoneReport && formattedPhone ? (
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Text component="span" variant="body2" sx={{ fontWeight: 500 }}>
              {nameize(report.search_type)}:
            </Text>
            <Text component="span" variant="body2" sx={{ fontWeight: 600 }}>
              {formattedPhone}
            </Text>
          </Box>
        ) : (
          displayName
        )
      }
      onClick={handleClick}
      sx={{
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.light}`,
        borderRadius: '100px',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.light,
        },
      }}
    />
  );
};
